import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer
} from 'react';
import Router, { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import {
  filterValuesSeparator,
  filterValuesSeparatorSplitRegex
} from 'shared-helpers/consts';
import { useApiData } from 'lib/api';
import { sanitizeFiltersFromRouterQuery } from 'lib/helpers';
import { LoadingIndicator } from 'components/common';

import { collectFilterFieldKeys } from './filterFieldKeys';

import type { FilterTypes, UserSavedFilter } from 'shared-types';
import type {
  GetUsersPageFilterContentsRes,
  GetUserSavedFiltersRes
} from 'shared-types/responses';
import type { FilterItem, FilterState } from '.';

/** Canonical applied filter params per filterId (draft lives in `state`). */
type FilterContext = {
  appliedParamsByFilterId: Record<string, Record<string, string>>;
  data: GetUsersPageFilterContentsRes;
  filters: Record<string, FilterItem[]>;
  onBeforeSubmitOrClear?: () => void;
  state: Record<string, FilterState>;
  savedFilters: UserSavedFilter[];
  savedFiltersAllowed: boolean;
  savedFiltersUpdate: () => Promise<void>;
  urlSyncByFilterId: Record<string, boolean>;
};

type Action =
  | { type: 'clearFilters'; filterId: string }
  | { type: 'removeAppliedFilterKey'; field: string; filterId: string }
  | { type: 'submitFilters'; filterId: string }
  | {
      type: 'syncAppliedFromRouter';
      filterId: string;
      params: Record<string, string>;
    }
  | {
      type: 'updateFilters';
      filterId: string;
      filters: FilterItem[];
      syncWithUrl?: boolean;
    }
  | {
      type: 'updateExternalData';
      data: Partial<GetUsersPageFilterContentsRes>;
    }
  | {
      type: 'updateState';
      filterId: string;
      state: Partial<FilterState> | null;
    }
  | {
      type: 'updateSavedFilters';
      savedFilters: UserSavedFilter[];
    };

const appliedParamsEqual = (
  a: Record<string, string> | undefined,
  b: Record<string, string>
): boolean => {
  const aKeys = Object.keys(a || {});
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;

  for (let i = 0; i < bKeys.length; i++) {
    const k = bKeys[i];
    if ((a || {})[k] !== b[k]) return false;
  }
  return true;
};

const applyPresentInAllToQuery = (
  query: Record<string, string | string[] | undefined>,
  nextPresentInAll: string | undefined
) => {
  if (nextPresentInAll) {
    query.presentInAll = nextPresentInAll;
  } else {
    delete query.presentInAll;
  }
};

const assertNoDuplicateFilterFields = (
  nextFilters: Record<string, FilterItem[]>
) => {
  const fieldToOwner: Record<string, string> = {};
  for (const [fid, items] of Object.entries(nextFilters)) {
    for (const key of collectFilterFieldKeys(items)) {
      if (fieldToOwner[key] && fieldToOwner[key] !== fid) {
        throw new Error(
          `Duplicate filter field "${key}" registered by filterId "${fid}" and "${fieldToOwner[key]}"`
        );
      }
      fieldToOwner[key] = fid;
    }
  }
};

/** Returns only `presentInAll` entries that belong to the given filter fields. */
const scopePresentInAllToFields = (
  presentInAll: string | string[] | undefined,
  fieldKeys: string[]
): string | undefined => {
  if (presentInAll === undefined || presentInAll === '') return undefined;

  const owned = new Set(fieldKeys);
  const raw = Array.isArray(presentInAll)
    ? presentInAll.join(filterValuesSeparator)
    : String(presentInAll);

  const parts = raw
    .split(filterValuesSeparatorSplitRegex)
    .filter((key) => key && owned.has(key));

  return parts.length ? parts.join(filterValuesSeparator) : undefined;
};

/**
 * Updates the shared `presentInAll` query param value for the given filter fields
 * while preserving entries that belong to other filters.
 */
const updatePresentInAllForFields = ({
  existingPresentInAll,
  fieldKeys,
  nextPresentInAllForFields
}: {
  existingPresentInAll: string | string[] | undefined;
  fieldKeys: string[];
  nextPresentInAllForFields?: string;
}): string | undefined => {
  const owned = new Set(fieldKeys);

  const preserved = (() => {
    // Keep keys that this filter does not own
    if (existingPresentInAll === undefined || existingPresentInAll === '') {
      return [] as string[];
    }
    const raw = Array.isArray(existingPresentInAll)
      ? existingPresentInAll.join(filterValuesSeparator)
      : String(existingPresentInAll);
    return raw
      .split(filterValuesSeparatorSplitRegex)
      .filter((key) => key && !owned.has(key));
  })();

  const fromThisFilter = nextPresentInAllForFields
    ? nextPresentInAllForFields
        .split(filterValuesSeparatorSplitRegex)
        .filter((key) => key && owned.has(key))
    : [];

  const merged = [...preserved, ...fromThisFilter];
  return merged.length ? merged.join(filterValuesSeparator) : undefined;
};

const reducer = (ctx: FilterContext, action: Action) => {
  if (action.type === 'syncAppliedFromRouter') {
    if (ctx.urlSyncByFilterId[action.filterId] === false) return ctx;

    const prev = ctx.appliedParamsByFilterId[action.filterId] || {};
    if (appliedParamsEqual(prev, action.params)) return ctx;

    return {
      ...ctx,
      appliedParamsByFilterId: {
        ...ctx.appliedParamsByFilterId,
        [action.filterId]: action.params
      }
    };
  }

  if (action.type === 'clearFilters') {
    const sync = ctx.urlSyncByFilterId[action.filterId] !== false;

    const hadState =
      ctx.state[action.filterId] &&
      Object.keys(ctx.state[action.filterId]).length > 0;

    const hadApplied =
      ctx.appliedParamsByFilterId[action.filterId] &&
      Object.keys(ctx.appliedParamsByFilterId[action.filterId]).length > 0;

    const hasLocalState = hadState || hadApplied;

    if (!sync) {
      if (!hasLocalState) return ctx;
      if (ctx.onBeforeSubmitOrClear) ctx.onBeforeSubmitOrClear();
    } else {
      const ownedFieldKeys = collectFilterFieldKeys(
        ctx.filters[action.filterId]
      );

      const query = sanitizeFiltersFromRouterQuery({
        query: Router.query,
        queryKeysToClear: ownedFieldKeys
      });

      const nextPresentInAll = updatePresentInAllForFields({
        existingPresentInAll: Router.query.presentInAll,
        fieldKeys: ownedFieldKeys
      });
      const prevPresentInAll = Router.query.presentInAll;
      applyPresentInAllToQuery(query, nextPresentInAll);

      const hasRemovedQueryParams =
        Object.keys(Router.query).some((key) => !(key in query)) ||
        prevPresentInAll !== query.presentInAll;

      if (!hasRemovedQueryParams && !hasLocalState) {
        return ctx;
      }

      if (hasRemovedQueryParams) {
        if (ctx.onBeforeSubmitOrClear) ctx.onBeforeSubmitOrClear();
        if (query.page) query.page = '1';

        Router.replace({ query }, undefined, { shallow: true });
      }
    }

    const newState = { ...ctx.state };
    delete newState[action.filterId];

    const newApplied = { ...ctx.appliedParamsByFilterId };
    delete newApplied[action.filterId];

    return { ...ctx, appliedParamsByFilterId: newApplied, state: newState };
  } else if (action.type === 'removeAppliedFilterKey') {
    const filterId = action.filterId;
    const sync = ctx.urlSyncByFilterId[filterId] !== false;

    const removeFieldFromPresentInAll = (
      presentInAll: string | undefined
    ): string | undefined => {
      if (!presentInAll) return undefined;

      const parts = presentInAll
        .split(filterValuesSeparatorSplitRegex)
        .filter((key) => key && key !== action.field);

      return parts.length ? parts.join(filterValuesSeparator) : undefined;
    };

    // clear the submitted/applied params
    const applied = { ...(ctx.appliedParamsByFilterId[filterId] || {}) };
    delete applied[action.field];

    const nextPresentInAll = removeFieldFromPresentInAll(applied.presentInAll);
    if (nextPresentInAll) {
      applied.presentInAll = nextPresentInAll;
    } else {
      delete applied.presentInAll;
    }

    // clear the draft filter state
    const nextFilterState = { ...ctx.state[filterId] };
    delete nextFilterState[action.field];
    if (nextFilterState.presentInAll?.[action.field]) {
      nextFilterState.presentInAll = { ...nextFilterState.presentInAll };
      delete nextFilterState.presentInAll[action.field];
      if (Object.keys(nextFilterState.presentInAll).length === 0) {
        delete nextFilterState.presentInAll;
      }
    }

    if (sync) {
      // keep the browser URL in sync
      const query = { ...Router.query };
      delete query[action.field];

      const nextQueryPresentInAll = removeFieldFromPresentInAll(
        query.presentInAll as string | undefined
      );
      if (nextQueryPresentInAll) {
        query.presentInAll = nextQueryPresentInAll;
      } else {
        delete query.presentInAll;
      }
      if (query.page) query.page = '1';

      Router.replace({ query }, undefined, { shallow: true });
    }

    return {
      ...ctx,
      appliedParamsByFilterId: {
        ...ctx.appliedParamsByFilterId,
        [filterId]: applied
      },
      state: {
        ...ctx.state,
        [filterId]: nextFilterState
      }
    };
  } else if (action.type === 'submitFilters') {
    const filterId = action.filterId;
    const sync = ctx.urlSyncByFilterId[filterId] !== false;
    const filterState = ctx.state[filterId] || {};
    const ownedFieldKeys = collectFilterFieldKeys(ctx.filters[filterId]);

    const valuesPerQueryParam = Object.keys(filterState).reduce(
      (queryParams, key) => {
        queryParams[key] = Object.keys(filterState[key]).join(
          filterValuesSeparator
        );
        return queryParams;
      },
      {} as Record<string, string>
    );

    const prevApplied = ctx.appliedParamsByFilterId[filterId] || {};
    const allKeys = Object.keys({ ...prevApplied, ...valuesPerQueryParam });

    let hasChanged = false;
    for (let i = 0; i < allKeys.length; i++) {
      const k = allKeys[i];
      if (prevApplied[k] !== valuesPerQueryParam[k]) {
        hasChanged = true;
        break;
      }
    }
    if (!hasChanged) return ctx;
    if (ctx.onBeforeSubmitOrClear) ctx.onBeforeSubmitOrClear();

    // Applied map should only store presentInAll entries owned by this filter instance
    const scopedPresentInAll = scopePresentInAllToFields(
      valuesPerQueryParam.presentInAll,
      ownedFieldKeys
    );
    const appliedForFilter = { ...valuesPerQueryParam };
    if (scopedPresentInAll) {
      appliedForFilter.presentInAll = scopedPresentInAll;
    } else {
      delete appliedForFilter.presentInAll;
    }

    const nextApplied = {
      ...ctx.appliedParamsByFilterId,
      [filterId]: appliedForFilter
    };
    if (!sync) return { ...ctx, appliedParamsByFilterId: nextApplied };

    const ownedQueryParams = { ...valuesPerQueryParam };
    delete ownedQueryParams.presentInAll;

    const query = sanitizeFiltersFromRouterQuery({
      query: Router.query,
      queryKeysToClear: ownedFieldKeys
    });
    if (query.page) query.page = '1';

    const nextPresentInAll = updatePresentInAllForFields({
      existingPresentInAll: Router.query.presentInAll,
      fieldKeys: ownedFieldKeys,
      nextPresentInAllForFields: valuesPerQueryParam.presentInAll
    });
    applyPresentInAllToQuery(query, nextPresentInAll);

    Router.replace({ query: { ...query, ...ownedQueryParams } }, undefined, {
      shallow: true
    });

    return { ...ctx, appliedParamsByFilterId: nextApplied };
  } else if (action.type === 'updateFilters') {
    const nextFilters = { ...ctx.filters, [action.filterId]: action.filters };
    if (process.env.NODE_ENV === 'development') {
      // show an error message in dev if trying to register
      // the same filter field in two different filters
      assertNoDuplicateFilterFields(nextFilters);
    }

    const syncWithUrl = action.syncWithUrl !== false;

    return {
      ...ctx,
      filters: nextFilters,
      urlSyncByFilterId: {
        ...ctx.urlSyncByFilterId,
        [action.filterId]: syncWithUrl
      }
    };
  } else if (action.type === 'updateExternalData') {
    return { ...ctx, data: { ...ctx.data, ...action.data } };
  } else if (action.type === 'updateState') {
    return { ...ctx, state: { ...ctx.state, [action.filterId]: action.state } };
  } else if (action.type === 'updateSavedFilters') {
    return { ...ctx, savedFilters: action.savedFilters };
  }

  return ctx;
};

const initialState: FilterContext = {
  appliedParamsByFilterId: {},
  data: null,
  filters: {},
  onBeforeSubmitOrClear: undefined,
  state: {},
  savedFilters: [],
  savedFiltersAllowed: false,
  savedFiltersUpdate: () => Promise.resolve(),
  urlSyncByFilterId: {}
};

const FiltersStateContext = createContext<FilterContext>(initialState);

const FilterDispatchContext = createContext<React.Dispatch<Action>>(() => {});

export interface FiltersProviderProps extends React.PropsWithChildren<object> {
  // limits products and groups to the ones the requester administers.
  // Ignored for superadmins and view-all-users, and teamOnly takes priority.
  administeredOnly?: boolean;
  filters: Partial<
    Record<
      Extract<
        FilterTypes,
        | 'achievements'
        | 'ambitions'
        | 'calendarEvents'
        | 'competenceProfiles'
        | 'competences'
        | 'courses'
        | 'groups'
        | 'paths'
        | 'supervisors'
        | 'userAttributes'
      >,
      boolean
    >
  >;
  onBeforeSubmitOrClear?: () => void;
  savedFiltersAllowed?: boolean;
  // limits the filter contents to the products and groups the logged in
  // supervisor's own supervisees are linked to
  teamOnly?: boolean;
}

const FiltersProvider = ({
  administeredOnly = false,
  children,
  filters,
  onBeforeSubmitOrClear,
  savedFiltersAllowed = false,
  teamOnly = false
}: FiltersProviderProps) => {
  const { language, user } = useReduxState();

  const router = useRouter();
  const currentPagePath = router.pathname;

  const { data, loading } = useApiData<GetUsersPageFilterContentsRes>({
    onlyIf: Boolean(user?.id),
    url:
      `/admin/users-page-filters?language=${language}` +
      `&achievements=${filters.achievements}` +
      `&ambitions=${filters.ambitions}` +
      `&calendarEvents=${filters.calendarEvents}` +
      `&competenceProfiles=${filters.competenceProfiles}` +
      `&competences=${filters.competences}` +
      `&courses=${filters.courses}` +
      `&groups=${filters.groups}` +
      `&paths=${filters.paths}` +
      `&supervisors=${filters.supervisors}` +
      `&userAttributes=${filters.userAttributes}` +
      (administeredOnly ? '&administeredOnly=true' : '') +
      (teamOnly ? '&teamOnly=true' : '')
  });

  const { data: savedFiltersData, updateData: updateSavedFiltersData } =
    useApiData<GetUserSavedFiltersRes>({
      onlyIf: savedFiltersAllowed && Boolean(user?.id),
      onDataUpdated: (data) => {
        dispatch({
          type: 'updateSavedFilters',
          savedFilters: data.filters || []
        });
      },
      url: `/admin/saved-filters?pagePath=${encodeURIComponent(
        currentPagePath
      )}`
    });

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    data: data || null,
    onBeforeSubmitOrClear
  });

  useEffect(() => {
    if (!loading) dispatch({ type: 'updateExternalData', data });
  }, [data, loading]);

  /**
   * Mirror URL → canonical applied params for groups that use `syncWithUrl`.
   * Depends on `router.asPath` so we run after shallow `replace` has updated the URL string
   * (avoids reading a stale `router.query` in the same tick as submit).
   */
  useEffect(() => {
    if (!router.isReady) return;

    const { filters: filterMap, urlSyncByFilterId } = state;

    Object.keys(filterMap).forEach((filterId) => {
      if (urlSyncByFilterId[filterId] === false) return;

      const ownedFieldKeys = collectFilterFieldKeys(filterMap[filterId]);

      const next: Record<string, string> = {};
      ownedFieldKeys.forEach((key) => {
        const v = router.query[key];
        if (v !== undefined && v !== '') {
          next[key] = Array.isArray(v)
            ? v.join(filterValuesSeparator)
            : String(v);
        }
      });

      const scopedPresentInAll = scopePresentInAllToFields(
        router.query.presentInAll,
        ownedFieldKeys
      );
      if (scopedPresentInAll) {
        next.presentInAll = scopedPresentInAll;
      }

      dispatch({ type: 'syncAppliedFromRouter', filterId, params: next });
    });
  }, [
    router.asPath,
    router.isReady,
    state.filters,
    state.urlSyncByFilterId,
    dispatch
  ]);

  const activeFilters = useMemo(
    () => ({
      savedFilters: savedFiltersData?.filters || [],
      savedFiltersAllowed,
      savedFiltersUpdate: updateSavedFiltersData
    }),
    [savedFiltersData, savedFiltersAllowed, updateSavedFiltersData]
  );

  const filterStateContextValue = useMemo(
    () => ({ ...state, ...activeFilters }),
    [state, activeFilters]
  );

  return (
    <FiltersStateContext.Provider value={filterStateContextValue}>
      <FilterDispatchContext.Provider value={dispatch}>
        {loading ? <LoadingIndicator /> : children}
      </FilterDispatchContext.Provider>
    </FiltersStateContext.Provider>
  );
};

/**
 * Custom hook to access the filter context
 * Provides access to filter state, operations and saved filters
 *
 * @returns The filter context object
 */
const useFiltersContext = () => {
  const state = useContext(FiltersStateContext);
  return state;
};

const useFiltersDispatch = () => {
  const dispatch = useContext(FilterDispatchContext);
  return dispatch;
};

const EMPTY_APPLIED_PARAMS: Record<string, string> = {};

/**
 * Canonical applied filter params for one filter group (same shape as URL query string values).
 * Always read from context; URL is mirrored when `syncWithUrl` is on.
 */
const useAppliedFilterParams = (filterId: string) => {
  const { appliedParamsByFilterId } = useFiltersContext();
  const applied = appliedParamsByFilterId[filterId];

  return useMemo(
    () => (applied ? { ...applied } : EMPTY_APPLIED_PARAMS),
    [applied, filterId]
  );
};

export {
  FiltersProvider,
  useAppliedFilterParams,
  useFiltersContext,
  useFiltersDispatch
};
